/**
 *
 * Jit Requests
 *
 */
import React, { useCallback, useEffect, useState } from 'react';

import { CloudProvider } from '@ariksa/inventory-core';
import { AccessRequestGetResponse, DecisionStatus } from '@ariksa/jit/api';
import { Box, Center, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

import {
  dateLabel,
  renderTimeStackedCell,
  StackedCell,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { ActionButton } from 'components/DataEntry';
import { IconTypes, MonitoringIcon } from 'components/Icons';
import { JitRequestSubComponent } from 'containers/Jit/JitRequests/Components/JitRequestSubComponent';
import {
  renderJitApprovers,
  renderJitRequestPermissions,
  renderJitRequestStatus,
} from 'containers/Jit/JitRequests/Components/utils';
import { ViewJITRequest } from 'containers/Jit/JitRequests/Components/ViewJITRequest';
import { selectJitRequests } from 'containers/Jit/JitRequests/selectors';
import { actions } from 'containers/Jit/JitRequests/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface JitRequestsTableProps {
  viewBy: string;
  decisionStatus?: DecisionStatus;
}

export const JitRequestsTable: React.FC<JitRequestsTableProps> = props => {
  const { viewBy, decisionStatus } = props;
  const dispatch = useDispatch();
  const [activeRow, setActiveRow] = useState<AccessRequestGetResponse>(
    {} as AccessRequestGetResponse,
  );
  const viewRequestDisclosure = useDisclosure();
  const { renderStackedAccountNameID } = useCloudAccountId();

  const { jitRequests } = useSelector(selectJitRequests);

  // The one place this listing's query is built. Approving or denying from the
  // drawer refetches through here so the reload keeps the filter the dropdown
  // is still showing.
  const loadRequests = useCallback(() => {
    dispatch(
      actions.getJitRequests({
        q: {
          page: jitRequests.page.info.page_number,
          size: jitRequests.page.info.page_size,
          decisionStatus,
        },
      }),
    );
  }, [dispatch, jitRequests.page.info, decisionStatus]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const requestAccount = useCallback(row => {
    return row?.access_request_details?.[row.cloud_type.toLowerCase()] ?? {};
  }, []);

  const columns: TableColumnProps<AccessRequestGetResponse>[] = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Requester</Box>,
      render: ({ row }) => {
        return (
          <StackedCell
            upper={row?.requester_display_name}
            lower={row?.requester_type}
            iconProps={{ resourceType: IconTypes.Okta }}
          />
        );
      },
    },
    ...(viewBy === 'for_cloud'
      ? [
          {
            header: 'Account',
            align: 'left',
            render: ({ row }) => {
              return renderStackedAccountNameID(row?.account_id, 'center');
            },
          },
        ]
      : [
          {
            header: 'Data Group',
            align: 'left',
            render: ({ row }) => {
              return (
                <StackedCell upper={row?.data_group_name} lower="Data Group" />
              );
            },
          },
        ]),
    {
      header: 'Permissions',
      align: 'left',
      render: ({ row }) => {
        return renderJitRequestPermissions(row);
      },
    },
    {
      header: 'Received',
      accessor: 'request_creation_time',
      align: 'left',
      render: ({ row, value }) => {
        return (
          <StackedCell
            upper={dateLabel(value)}
            lower={dayjs.utc(value).local().format('hh:mm A')}
            showLowerTooltip={false}
            showUpperTooltip={false}
          />
        );
      },
    },
    {
      header: 'Request Start',
      accessor: 'requested_start_time',
      align: 'left',
      render: renderTimeStackedCell,
    },
    {
      header: 'Approver(s)',
      accessor: 'approvers',
      align: 'left',
      render: ({ value }) => renderJitApprovers(value),
    },
    {
      header: 'Status',
      accessor: 'status',
      align: 'left',
      // This is the approver's queue, so a pending request is work waiting on
      // them rather than a state they are watching.
      render: ({ row }) => renderJitRequestStatus({ row }, 'For Review'),
    },
    {
      header: 'Actions',
      render: ({ row }) => {
        return (
          <Center w="full">
            <ActionButton
              label="Review request"
              icon={<MonitoringIcon p={0.5} />}
              onClick={() => {
                viewRequestDisclosure.onOpen();
                setActiveRow(row);
              }}
              p={0.5}
            />
          </Center>
        );
      },
    },
  ];

  return (
    <>
      <Box flex={1}>
        <Table
          columns={columns}
          subComponent={({ row, expanded }) => {
            return (
              expanded && (
                <JitRequestSubComponent
                  row={row}
                  requestAccount={requestAccount}
                />
              )
            );
          }}
          data={
            viewBy === 'for_cloud'
              ? jitRequests.data.filter(
                  r => r.cloud_type !== CloudProvider.Snowflake,
                )
              : jitRequests.data.filter(
                  r => r.cloud_type === CloudProvider.Snowflake,
                )
          }
          pagination={{
            totalCount: jitRequests.page.totalCount ?? 0,
            pageInfo: jitRequests.page.info,
            onChange: pageInfo =>
              dispatch(actions.updateJitRequestsPagination(pageInfo)),
          }}
          isLoading={jitRequests.isLoading}
          noDataMessage={
            <Center h={'160px'}>No pending access requests</Center>
          }
        />
      </Box>
      {viewRequestDisclosure.isOpen && (
        <ViewJITRequest
          isOpen={viewRequestDisclosure.isOpen}
          onClose={viewRequestDisclosure.onClose}
          row={activeRow}
          viewBy={viewBy}
          onActionSuccess={loadRequests}
        />
      )}
    </>
  );
};
